import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import loadable from '@loadable/component';
import { Breadcrumb, HrTag, VapImage, FlexBox, Typography } from '@quikr/ui';
import { extractImgUrl } from '@quikr/ui/src/helpers/common';
import { msiteAdsBelowDesc } from '../../../googleAdConfig/core';
import VapInfo from '../VapInfo';
import VapExtrainfo from '../VapExtrainfo';
import { getAction } from '../../../helpers/action';
import { useGa } from '../../../hooks';

const AndroidDownloadPopup = loadable(() => import('../AndroidDownloadPopup'), { ssr: false });
const VapLoadables = loadable(() => import('../VapLoadables'), { ssr: false });
const GoogleAd = loadable(() => import('@quikr/ui/src/common/GoogleAd'), { ssr: false });
const ContactForm = loadable(() => import('../ContactForm'), { ssr: false });
const SellerDetails = loadable(() => import('../SellerDetails'), { ssr: false });
const MobileGallery = loadable(() => import('@quikr/ui/src/common/MobileGallery'), { ssr: false });
const DesktopGallery = loadable(() => import('@quikr/ui/src/common/Gallery'), {
  ssr: false
});
const ChatForm = loadable(() => import('../ChatForm'), { ssr: false });

const GoogleAdSecVert = styled(GoogleAd)`
  padding: 20px 0;
  text-align: center;
  position: relative;
  ${({ border }) =>
  border &&
    css`
      padding-bottom: 24px;
      margin-bottom: 30px;
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: -12px;
        width: calc(100% + 24px);
        border-bottom: 1px solid ${({ theme }) => theme.colors.Neutral[300]};
      }
    `}
`;

const Hr = styled(HrTag)`
  @media screen and (max-width: 767px) {
    margin: 0px -12px;
  }
`;

const CommonModule = ({ data: { abRandValue, isAndroid, isMobile, ad: adData } = {} }) => {
  const [showGallery, setshowGallery] = useState(false);
  const [androidDownloadPopup, setAndroidDownloadPopup] = useState({
    commonRedirectionGA: () => {},
    show: false,
    title: '',
    description: '',
    redirectOnClose: false,
    redirectUrl: '',
    isForQApp: false
  });
  const [showCallModal, setShowCallModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const { TriggerGaEvent } = useGa();
  const {
    images = [],
    adstyle,
    alttitle,
    title,
    user: adOwner,
    cat,
    subcat,
    city,
    breadcrumbs = [],
    price,
    actions
  } = adData;
  const galleryImages = {
    images: [],
    isTabsDisplay: false
  };
  const vapImages = images.map((image, index, obj) => {
    galleryImages.images.push(extractImgUrl(image, 'lg'));
    if (isMobile) {
      return extractImgUrl(image, 'nr');
    } else {
      if (Object.keys(obj).length >= 5) {
        if (index < 2) {
          return extractImgUrl(image, 'nr');
        }
        return extractImgUrl(image, 'gv');
      }
      return extractImgUrl(image);
    }
  });

  useEffect(() => {
    const Action = getAction().toLowerCase();
    if (Action) {
      switch (Action) {
        case 'contact':
          setShowCallModal(true);
          break;
        case 'chat':
          setShowChatModal(true);
          break;
        case 'whatsapp':
          setShowWhatsappModal(true);
          break;
        default:
          break;
      }
    }
  }, []);

  const toggleGallery = value => {
    if (vapImages.length > 0) {
      setshowGallery(value);
    }
  };

  const handleCallToggle = () => {
    setShowCallModal(!showCallModal);
    if (!showCallModal && showGallery) fireVAPGa('galleryCtaSection_call_init', 'Gallery');
  };

  const handleChatToggle = () => {
    setShowChatModal(!showChatModal);
    if (!showChatModal && showGallery) fireVAPGa('galleryCtaSection_chat_init', 'Gallery');
  };

  const handleWhatsappToggle = () => {
    setShowWhatsappModal(!showWhatsappModal);
    if (!showWhatsappModal && showGallery) fireVAPGa('galleryCtaSection_Whatsapp_init', 'Gallery');
  };

  const fireVAPGa = (eventLabel, eventCat = 'VAP') => {
    TriggerGaEvent({
      category: eventCat,
      label: eventLabel,
      cityName: city.name,
      catName: cat.name,
      subcatName: subcat.name
    });
  };

  // 152222 - Jaipur, 22 - Ahmedabad
  const allowRedirectionToQApp = isMobile && isAndroid && abRandValue <= -1;
  const handleRedirectionToQApp =
    allowRedirectionToQApp &&
    (action => {
      setAndroidDownloadPopup({
        commonRedirectionGA: () => fireVAPGa(`QAPP_VAP_${action}_REDIRECT`),
        description: 'Download Our App to Respond to Ads',
        isForQApp: true,
        show: true,
        redirectOnClose: true,
        redirectUrl: `https://play.google.com/store/apps/details?id=com.quikr&utm_source=VAP_${action.toLowerCase()}&utm_campaign=QAPP_VAP_REDIRECT&utm_medium=${cat.name.replaceAll(
          /\s+/g,
          '_'
        )}`,
        title: 'Quikr is Better on App!'
      });
    });

  const gutter = isMobile ? 12 : 20;
  return (
    <>
      <FlexBox.Row margin={gutter}>
        <FlexBox.Column md={7} sm={7} xs={12} gutterWidth={gutter}>
          {!isMobile && <Breadcrumb data={breadcrumbs} />}
          <VapImage
            images={vapImages}
            isMobile={isMobile}
            adType={adstyle}
            title={alttitle || title}
            openGallery={toggleGallery}
          />
          {isMobile && (
            <VapInfo
              data={adData}
              openCall={handleCallToggle}
              openChat={handleChatToggle}
              openWhatsapp={handleWhatsappToggle}
              redirectToQApp={handleRedirectionToQApp}
            />
          )}
          <VapExtrainfo data={adData} type="common" />
          <GoogleAdSecVert config={msiteAdsBelowDesc(cat.name)} />
          <Hr />
          <Typography tagName="h2" modifiers="h2" className="vap__subtitle">
            Posted By
          </Typography>
          <SellerDetails
            open={actions.top.includes('WHATSAPP') ? handleWhatsappToggle : handleChatToggle}
            sellerData={adOwner}
            fireGa={fireVAPGa}
            showButton={
              actions.top.includes('WHATSAPP')
                ? 'WHATSAPP'
                : actions.top.includes('CHAT')
                  ? 'CHAT'
                  : ''
            }
            isMobile={isMobile}
            redirectToQApp={handleRedirectionToQApp}
          />
          <VapLoadables
            type="Primary"
            adData={adData}
            userId={String(adData.user.id)}
            fireGa={fireVAPGa}
          />
        </FlexBox.Column>
        {!isMobile && (
          <FlexBox.Column md={5} sm={5} hiddenXs gutterWidth={20}>
            <VapInfo
              data={adData}
              openCall={handleCallToggle}
              openChat={handleChatToggle}
              openWhatsapp={handleWhatsappToggle}
              redirectToQApp={handleRedirectionToQApp}
            />
          </FlexBox.Column>
        )}
      </FlexBox.Row>
      {/* {!props.isMobile && <GoogleAdSecVert config={adsElementsSec3} />} */}
      {androidDownloadPopup.show && (
        <AndroidDownloadPopup
          commonRedirectionGA={androidDownloadPopup.commonRedirectionGA}
          description={androidDownloadPopup.description}
          city={city}
          isForQApp={androidDownloadPopup.isForQApp}
          onClose={() => setAndroidDownloadPopup(prev => ({ ...prev, show: false }))}
          redirectOnClose={androidDownloadPopup.redirectOnClose}
          redirectUrl={androidDownloadPopup.redirectUrl}
          showPopup={androidDownloadPopup.show}
          title={androidDownloadPopup.title}
          fireGa={fireVAPGa}
        />
      )}
      {showCallModal && (
        <ContactForm
          title="Call"
          onClose={handleCallToggle}
          cat={cat}
          subcat={subcat}
          fireGa={fireVAPGa}
          privacy={adData.private}
        />
      )}
      {showGallery &&
        (isMobile ? (
          <MobileGallery
            close={toggleGallery}
            galleryImages={galleryImages}
            title={alttitle || title}
            price={price}
            actions={actions.gallery}
            openCall={handleCallToggle}
            openChat={handleChatToggle}
            openWhatsapp={handleWhatsappToggle}
            redirectToQApp={handleRedirectionToQApp}
          />
        ) : (
          <DesktopGallery
            close={toggleGallery}
            galleryImages={galleryImages}
            title={alttitle || title}
            price={price}
            actions={actions.gallery}
            openCall={handleCallToggle}
            openChat={handleChatToggle}
            openWhatsapp={handleWhatsappToggle}
          />
        ))}
      {showChatModal && <ChatForm adData={adData} onClose={handleChatToggle} fireGa={fireVAPGa} />}
      {showWhatsappModal && (
        <ContactForm
          title="Whatsapp"
          onClose={handleWhatsappToggle}
          cat={cat}
          subcat={subcat}
          fireGa={fireVAPGa}
        />
      )}
    </>
  );
};

CommonModule.propTypes = {
  data: PropTypes.object,
  staticContext: PropTypes.object
};

CommonModule.defaultProps = {};

export default CommonModule;
